import { UAApps } from '@unifyapps/defs/types/apps';
import type { AutomationInputsType, NodeContextType } from '@unifyapps/defs/types/dataSource';
import type { InterfaceEntity } from '@unifyapps/defs/types/interface';
import { UAResources } from '@unifyapps/defs/types/resources';
import type { ExecuteWorkflowNodeRequest } from '@unifyapps/network/generated/models/executeWorkflowNodeRequest';
import { useGetQueryMeta } from '@unifyapps/network/hooks/useGetQueryMeta';
import type { QueryFunction } from '@unifyapps/network/react-query';
import { keepPreviousData, useQuery } from '@unifyapps/network/react-query';
import type { ErrorType } from '@unifyapps/network/utils/fetch';
import type { JSONSchema7 } from 'json-schema';
import _omit from 'lodash/omit';
import { useCallback, useMemo } from 'react';
import { useOfflineDataSourceHandler } from '../../../../../../offline-mode/hooks/useOfflineDataSourceHandler';
import type { QueryRequestResult } from '../../../../../stores/GlobalStateStore';
import { useAdaptDataSourceResponse } from '../../../hooks/useAdaptDataSourceResponse';
import type { DataSourceQueryOptions } from '../../../types';
import {
  getCallableAutomationId,
  getExecutionInstanceId,
} from '../../../utils/callableAutomationRun';
import type { DebugParams, ExecuteWorkflowNodeQueryKeyType } from '../api';
import {
  executeAuthWorkflowNode,
  executeMultiPartFormDataWorkflowNode,
  executePlatformByUAOperation,
  executeRunGetSchemaWorkflowNode,
  executeUpdatePasswordWorkflowNode,
  executeWorkflowNode,
  useExecuteWorkflowNodeQueryKeyMemo,
} from '../api';
import clientDirectApi from '../api/clientDirectApi';
import useDSWorkflowNodePayload from './useDSWorkflowNodePayload';

type UseDataSourceWorkflowNodeParams = {
  payload: ExecuteWorkflowNodeRequest;
  debugParams: DebugParams;
  queryOptions?: Partial<DataSourceQueryOptions>;
  enabled?: boolean;
  transformer?: string;
  options?: {
    useDirectApi?: boolean;
    isTransformerConfigured?: boolean;
    isBuilderMode?: boolean;
    pathToOmitFromQueryKey?: string[];
  };
  interfaceRecord?: InterfaceEntity;
};

export type DataSourceWorkflowNodeResponse = Awaited<
  ReturnType<typeof executeWorkflowNode> | ReturnType<typeof executePlatformByUAOperation>
>;
export type DataSourceWorkflowNodeError = ErrorType<unknown>;

export type DataSourceWorkflowNodeQueryRequestResult = QueryRequestResult & {
  outputSchema?: JSONSchema7;
  // raw ids for the Pulse "view run" link — the URL is built later, on the builder-only pulse path
  automationId?: string;
  executionInstanceId?: string;
};

function useDataSourceWorkflowNode({
  payload,
  debugParams,
  queryOptions,
  enabled,
  options,
  interfaceRecord,
}: UseDataSourceWorkflowNodeParams): DataSourceWorkflowNodeQueryRequestResult {
  const transformedPayload = useDSWorkflowNodePayload({
    payload,
  });
  const isFormData = transformedPayload instanceof FormData;
  const appName = payload.context?.appName as UAApps | undefined;
  const queryMeta = useGetQueryMeta();

  const isAuthByUnifyApps = appName === UAApps.AuthByUA;
  const isPlatformByUnifyApps = appName === UAApps.PlatformByUA;
  const isUpdatePassword =
    appName === UAApps.StandardEntities &&
    payload.context?.resourceName === UAResources.StandardEntitiesResetPassword;
  const isDirectApi = Boolean(options?.useDirectApi);

  const isRunAndGetSchema = Boolean(options?.isBuilderMode && options.isTransformerConfigured);

  // offline mode handler
  const { shouldUseOfflineMode, offlineQueryFunction } = useOfflineDataSourceHandler({
    payload,
    interfaceRecord,
  });

  const queryKeyType = useMemo<ExecuteWorkflowNodeQueryKeyType>(() => {
    if (shouldUseOfflineMode) return 'offline';
    if (isFormData) return 'formdata';
    if (isAuthByUnifyApps && queryMeta?.isPublicPage) return 'auth';
    if (isUpdatePassword) return 'api-update-password';
    if (isRunAndGetSchema) return 'api-rungetschema';

    return 'api';
  }, [
    queryMeta,
    shouldUseOfflineMode,
    isAuthByUnifyApps,
    isFormData,
    isUpdatePassword,
    isRunAndGetSchema,
  ]);

  const queryKey = useExecuteWorkflowNodeQueryKeyMemo(
    _omit(payload, options?.pathToOmitFromQueryKey ?? []),
    debugParams,
    queryKeyType,
  );

  const queryFunction = useCallback<
    QueryFunction<DataSourceWorkflowNodeResponse, [DataSourceWorkflowNodeError]>
  >(
    ({ meta }) => {
      const _debugParams = {
        ...debugParams,
        meta: { ...(meta ?? {}), ...(debugParams?.meta ?? {}) },
      };

      if (isFormData) {
        return executeMultiPartFormDataWorkflowNode(transformedPayload, _debugParams);
      }

      // handle offline mode first
      if (shouldUseOfflineMode) {
        return offlineQueryFunction(transformedPayload);
      }

      /**
       * ticket: https://unifyapps.monday.com/boards/1434760518/views/40609862/pulses/2602822450
       * Had a use case were authByUnifyApps node was used on private page for
       * updating/resetting of password by sending the otp to logged in user.
       */
      if (isAuthByUnifyApps && _debugParams.meta.isPublicPage) {
        return executeAuthWorkflowNode(transformedPayload, _debugParams);
      }

      if (isUpdatePassword) {
        return executeUpdatePasswordWorkflowNode(transformedPayload, _debugParams);
      }

      if (isPlatformByUnifyApps) {
        return executePlatformByUAOperation(transformedPayload, _debugParams);
      }

      if (isDirectApi) {
        return clientDirectApi(transformedPayload, _debugParams);
      }

      if (isRunAndGetSchema) {
        return executeRunGetSchemaWorkflowNode(transformedPayload, _debugParams);
      }

      return executeWorkflowNode(transformedPayload, _debugParams);
    },
    [
      shouldUseOfflineMode,
      offlineQueryFunction,
      debugParams,
      isAuthByUnifyApps,
      isDirectApi,
      isFormData,
      isPlatformByUnifyApps,
      isUpdatePassword,
      isRunAndGetSchema,
      transformedPayload,
    ],
  );

  // These datasources read from the local SQLite DB. They remount a lot (the dependency graph keeps
  // restaging them), and each remount would re-read the DB. `refetchOnMount: true` skips that re-read
  // when we already read within the last 5s, unlike the base `'always'` which re-reads every time.
  //
  // Retry is turned off too. A failed offline read comes back stamped as a 500, so React Query would
  // normally retry it — but retrying a local read just returns the same result, so there's no point.
  const offlineQueryOptions = useMemo(
    () => (shouldUseOfflineMode ? ({ refetchOnMount: true, retry: false } as const) : undefined),
    [shouldUseOfflineMode],
  );

  const {
    isLoading,
    isFetching,
    error: fetchError,
    data: initialData,
    refetch,
  } = useQuery<
    Awaited<
      ReturnType<typeof executeWorkflowNode> | ReturnType<typeof executePlatformByUAOperation>
    >,
    DataSourceWorkflowNodeError,
    DataSourceWorkflowNodeResponse
  >({
    queryFn: queryFunction,
    queryKey,
    enabled,
    placeholderData: keepPreviousData,
    // we are keeping refetchOnMount: 'always' to ensure that because we want to support automatic data sources for default values.
    // if this is not passed, the automatic queries are unmounted on navigating to different pages, but when coming back to the same page quickly, sometimes the query are not re triggered.
    refetchOnMount: 'always',
    ...queryOptions,
    ...offlineQueryOptions,
  });

  const { rawData, data, outputSchema } = useAdaptDataSourceResponse({
    isTransformerConfigured: Boolean(options?.isTransformerConfigured),
    initialData,
    payload,
    appName,
  });

  const automationId = getCallableAutomationId(
    payload.context as NodeContextType | undefined,
    payload.inputs as AutomationInputsType | undefined,
  );

  return {
    error: fetchError?.payload,
    isLoading,
    isFetching,
    rawData,
    data,
    inputs: payload.inputs,
    queryKey,
    refetch,
    outputSchema,
    automationId,
    executionInstanceId: getExecutionInstanceId(initialData),
  };
}

export default useDataSourceWorkflowNode;
